import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import callOracleApi from '../../utils/call-oracle-api.js';
import '../../styles/MissionRequests.css';

const EXECUTIVE_OBJECTIVES = [
    'Process & Culture 1: Enhance the Employee Experience',
    'Customer 1: Reduce Customer Churn',
    'Customer 2: Drive Marketing Efficencies',
    'Customer 3: Improve SC Recruiting and Onboarding Results',
    'Growth, Tech & Disruption 1: Increase Copilot Usage',
    'Growth, Tech & Disruption 2: Increase IT System Reliability & Performance',
    'Growth, Tech & Disruption 3: Document 24 month Strategic Plan',
    'Financial Commitments 1: Achieve 2026 Financial Commitments'
];

const DEPARTMENTS = [
    'Accounting',
    'Brand Promise',
    'Client Onboarding',
    'Copilot',
    'Employee Onboarding',
    'Execs',
    'Legal',
    'Marketing',
    'Ops / RLs / TLs',
    'Product Leads',
    'People Services',
    'Sales',
    'Other'
];

const INITIAL = {
    title: '',
    problemStatement: '',
    description: '',
    estimatedWeeks: '',
    executiveObjective: '',
    successMeasure: '',
    estimatedRoi: '',
    potentialCaptain: 'Undecided',
    potentialCaptainOther: '',
    interestedMembers: '',
    involvedDepartments: [],
    involvedDepartmentsOther: '',
    innovationPriority: '',
    innovationNeeds: '',
    hubPriority: '',
    hubNeeds: ''
};

const MissionSubmitPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);

    const set = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const toggleDept = (dept) => {
        setForm((prev) => {
            const has = prev.involvedDepartments.includes(dept);
            return {
                ...prev,
                involvedDepartments: has
                    ? prev.involvedDepartments.filter((d) => d !== dept)
                    : [...prev.involvedDepartments, dept]
            };
        });
    };

    const validate = () => {
        const required = [
            ['title', 'Working title'],
            ['problemStatement', 'Problem statement'],
            ['description', 'Mission description'],
            ['estimatedWeeks', 'Estimated weeks'],
            ['executiveObjective', 'Executive objective'],
            ['successMeasure', 'Success measure'],
            ['estimatedRoi', 'Estimated ROI'],
            ['interestedMembers', 'Interested members'],
            ['innovationPriority', 'Innovation priority'],
            ['hubPriority', 'Hub priority']
        ];
        const missing = required.find(
            ([field]) =>
                form[field] === '' ||
                form[field] === null ||
                form[field] === undefined
        );
        if (missing) {
            toast.error(`${missing[1]} is required`);
            return false;
        }
        if (form.involvedDepartments.length === 0) {
            toast.error('Select at least one department');
            return false;
        }
        if (
            form.potentialCaptain === 'Other' &&
            !form.potentialCaptainOther.trim()
        ) {
            toast.error('Please specify the potential captain');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                potentialCaptain:
                    form.potentialCaptain === 'Other'
                        ? form.potentialCaptainOther
                        : form.potentialCaptain,
                estimatedWeeks: Number(form.estimatedWeeks),
                innovationPriority: Number(form.innovationPriority),
                hubPriority: Number(form.hubPriority)
            };
            delete payload.potentialCaptainOther;

            const response = await callOracleApi(
                '/mission-requests',
                'POST',
                payload
            );
            const data = await response.json();
            if (response.ok && data.success) {
                toast.success(
                    'Mission submitted! Leadership will review within 2 weeks.'
                );
                navigate('/mission-requests/my');
            } else {
                toast.error(data.message || 'Submission failed');
            }
        } catch {
            toast.error('Network error — please try again');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mr-page">
            {/* Banner */}
            <div
                className="mr-section-card"
                style={{ marginBottom: '0.75rem' }}>
                <h1 className="mr-section-title">New Mission Submission</h1>
                <p className="mr-section-desc" style={{ marginTop: '0.4rem' }}>
                    Please fill the form out in its entirety with as much detail
                    as possible. Don&apos;t be discouraged if you are not sure
                    how to answer — reach out to leadership and we&apos;re happy
                    to help.
                </p>
                <p
                    className="mr-section-desc"
                    style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                    Submissions are reviewed by leadership in a 2-week cycle.
                    Once complete, the outline will be returned to you.
                </p>
            </div>

            <form
                onSubmit={handleSubmit}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                }}>
                {/* Working Title */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Working title of Mission
                        <span className="mr-required">*</span>
                    </label>
                    <input
                        className="form-input"
                        value={form.title}
                        onChange={(e) => set('title', e.target.value)}
                        placeholder="Your answer"
                    />
                </div>

                {/* Problem */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        What problem does this Mission intend to solve?
                        <span className="mr-required">*</span>
                    </label>
                    <textarea
                        className="form-textarea"
                        rows={3}
                        value={form.problemStatement}
                        onChange={(e) =>
                            set('problemStatement', e.target.value)
                        }
                        placeholder="Your answer"
                    />
                </div>

                {/* Description */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Please describe the Mission
                        <span className="mr-required">*</span>
                    </label>
                    <textarea
                        className="form-textarea"
                        rows={4}
                        value={form.description}
                        onChange={(e) => set('description', e.target.value)}
                        placeholder="Your answer"
                    />
                </div>

                {/* Estimated Weeks */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Estimated weeks it will take to complete
                        <span className="mr-required">*</span>
                    </label>
                    <div className="mr-scale-labels">
                        <span>1 week</span>
                        <span>10 weeks</span>
                    </div>
                    <div className="mr-scale">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <label
                                key={n}
                                className={`week-option ${
                                    form.estimatedWeeks === n ? 'selected' : ''
                                }`}>
                                <input
                                    type="radio"
                                    name="estimatedWeeks"
                                    value={n}
                                    checked={form.estimatedWeeks === n}
                                    onChange={() => set('estimatedWeeks', n)}
                                    style={{ display: 'none' }}
                                />
                                {n}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Executive Objective */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        What Executive Objective does this Mission relate to?
                        <span className="mr-required">*</span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {EXECUTIVE_OBJECTIVES.map((obj) => (
                            <label key={obj} className="mr-option">
                                <input
                                    type="radio"
                                    name="executiveObjective"
                                    value={obj}
                                    checked={form.executiveObjective === obj}
                                    onChange={() =>
                                        set('executiveObjective', obj)
                                    }
                                />
                                {obj}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Success Measure */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        How do we measure success on this Mission?
                        <span className="mr-required">*</span>
                    </label>
                    <textarea
                        className="form-textarea"
                        rows={3}
                        value={form.successMeasure}
                        onChange={(e) => set('successMeasure', e.target.value)}
                        placeholder="Your answer"
                    />
                </div>

                {/* ROI */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Estimated ROI (return on investment) &amp; explain
                        <span className="mr-required">*</span>
                    </label>
                    <p className="mr-hint">
                        Example: Saves 5 minutes on each ticket by automating X.
                    </p>
                    <textarea
                        className="form-textarea"
                        rows={3}
                        value={form.estimatedRoi}
                        onChange={(e) => set('estimatedRoi', e.target.value)}
                        placeholder="Your answer"
                    />
                </div>

                {/* ── Mission Team section ── */}
                <div
                    className="mr-section-card"
                    style={{ marginTop: '0.5rem' }}>
                    <h2 className="mr-section-title">Mission Team</h2>
                </div>

                {/* Potential Captain */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Potential Captain
                        <span className="mr-required">*</span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label className="mr-option">
                            <input
                                type="radio"
                                name="potentialCaptain"
                                value="Undecided"
                                checked={form.potentialCaptain === 'Undecided'}
                                onChange={() =>
                                    set('potentialCaptain', 'Undecided')
                                }
                            />
                            Undecided
                        </label>
                        <label className="mr-option">
                            <input
                                type="radio"
                                name="potentialCaptain"
                                value="Other"
                                checked={form.potentialCaptain === 'Other'}
                                onChange={() =>
                                    set('potentialCaptain', 'Other')
                                }
                            />
                            Other:
                            <input
                                className="form-input"
                                style={{
                                    flex: 1,
                                    marginLeft: 8,
                                    padding: '0.25rem 0.5rem'
                                }}
                                value={form.potentialCaptainOther}
                                onChange={(e) =>
                                    set('potentialCaptainOther', e.target.value)
                                }
                                disabled={form.potentialCaptain !== 'Other'}
                                placeholder="Name or email"
                            />
                        </label>
                    </div>
                </div>

                {/* Members */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Members interested in working on Mission
                        <span className="mr-required">*</span>
                    </label>
                    <p className="mr-hint">n/a if not sure</p>
                    <textarea
                        className="form-textarea"
                        rows={2}
                        value={form.interestedMembers}
                        onChange={(e) =>
                            set('interestedMembers', e.target.value)
                        }
                        placeholder="Your answer"
                    />
                </div>

                {/* Departments */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        What other departments would need to be involved in the
                        Mission?
                        <span className="mr-required">*</span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {DEPARTMENTS.map((dept) => (
                            <label key={dept} className="mr-option">
                                <input
                                    type="checkbox"
                                    checked={form.involvedDepartments.includes(
                                        dept
                                    )}
                                    onChange={() => toggleDept(dept)}
                                />
                                {dept}
                            </label>
                        ))}
                    </div>
                    {form.involvedDepartments.includes('Other') && (
                        <input
                            className="form-input"
                            style={{ marginTop: '0.5rem' }}
                            placeholder="Specify other department"
                            value={form.involvedDepartmentsOther}
                            onChange={(e) =>
                                set('involvedDepartmentsOther', e.target.value)
                            }
                        />
                    )}
                </div>

                {/* ── Development Needs section ── */}
                <div
                    className="mr-section-card"
                    style={{ marginTop: '0.5rem' }}>
                    <h2 className="mr-section-title">Development Needs</h2>
                </div>

                {/* Innovation Priority */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        What priority of development with Innovation would this
                        Mission have?
                        <span className="mr-required">*</span>
                    </label>
                    <div className="mr-scale-labels">
                        <span>0 — No Innovation dev needed</span>
                        <span>5 — Highest priority</span>
                    </div>
                    <div className="mr-scale">
                        {[0, 1, 2, 3, 4, 5].map((n) => (
                            <label
                                key={n}
                                className={`week-option ${
                                    form.innovationPriority === n
                                        ? 'selected'
                                        : ''
                                }`}>
                                <input
                                    type="radio"
                                    name="innovationPriority"
                                    value={n}
                                    checked={form.innovationPriority === n}
                                    onChange={() =>
                                        set('innovationPriority', n)
                                    }
                                    style={{ display: 'none' }}
                                />
                                {n}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Innovation Needs */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Describe Innovation development needs.
                    </label>
                    <textarea
                        className="form-textarea"
                        rows={2}
                        value={form.innovationNeeds}
                        onChange={(e) => set('innovationNeeds', e.target.value)}
                        placeholder="Your answer"
                    />
                </div>

                {/* Hub Priority */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        What priority of development with the Hub would this
                        Mission have?
                        <span className="mr-required">*</span>
                    </label>
                    <div className="mr-scale-labels">
                        <span>0 — No Hub dev needed</span>
                        <span>5 — Highest priority</span>
                    </div>
                    <div className="mr-scale">
                        {[0, 1, 2, 3, 4, 5].map((n) => (
                            <label
                                key={n}
                                className={`week-option ${
                                    form.hubPriority === n ? 'selected' : ''
                                }`}>
                                <input
                                    type="radio"
                                    name="hubPriority"
                                    value={n}
                                    checked={form.hubPriority === n}
                                    onChange={() => set('hubPriority', n)}
                                    style={{ display: 'none' }}
                                />
                                {n}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Hub Needs */}
                <div className="mr-form-card">
                    <label className="mr-question-label">
                        Describe Hub development needs.
                    </label>
                    <textarea
                        className="form-textarea"
                        rows={2}
                        value={form.hubNeeds}
                        onChange={(e) => set('hubNeeds', e.target.value)}
                        placeholder="Your answer"
                    />
                </div>

                {/* Actions */}
                <div
                    style={{
                        display: 'flex',
                        gap: '0.75rem',
                        justifyContent: 'flex-end',
                        paddingTop: '0.5rem'
                    }}>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => navigate(-1)}
                        disabled={submitting}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Submit Mission'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MissionSubmitPage;
